import React from 'react';
import {
  AttributeInputCallback,
  ConfirmationCallback,
  FRCallback,
  PasswordCallback,
  ReCaptchaCallback,
  SelectIdPCallback,
  TextOutputCallback,
  ValidatedCreatePasswordCallback,
} from '@forgerock/javascript-sdk';
import {
  AssociateAccountIntroduction,
  EditablePreviousInput,
  ErrorMessage,
  OneTimePassword,
  Password,
  PasswordCreation,
  SubmitButton,
  Captcha,
} from 'Components';
import { getErrorMessageByKey, mapAttributeInputCallbackToComponent, TrackingService } from 'Services';
import { SocialButtons } from 'Components/SocialButtons/SocialButtons';
import { BloctelLegalMention } from 'Components/BloctelLegalMention/BloctelLegalMention';
import { Origin } from 'Constants';

export const DEFAULT_MESSAGE = 'Default message';
const MESSAGE_TYPE_SUCCESS = '0';
export const MESSAGE_TYPE_ERROR = '2';
const MESSAGE_VALUE_ACCOUNT_LOCKED = 'accountLocked';
const MESSAGE_VALUE_BLOCTEL_LEGAL_MENTION = 'bloctel_legal_mention';

export const BOTTOM = '_bottom';
const FORGEROCK_ERROR_KEYS = ['suspendedUuidExpired'];

export const isOtpCallback = (callback?: FRCallback) =>
  callback instanceof ConfirmationCallback && ['otp', 'Send new OTP'].some((option) => callback.getOptions().includes(option));

const shouldDisplayCustomErrorMessage = (callback: TextOutputCallback) =>
  FORGEROCK_ERROR_KEYS.includes(callback.getMessage()) || callback.getMessage().startsWith('DecisionNodeScript');

const getForgerockErrorMessage = (callback: TextOutputCallback) => {
  if (callback.getMessage() === 'UnexpectedError') {
    return 'Une erreur technique est survenue. Merci de réessayer dans quelques instants.';
  }
  return callback.getMessage();
};

const getErrorMessage = (callback: TextOutputCallback) =>
  shouldDisplayCustomErrorMessage(callback) ? getErrorMessageByKey(callback.getMessage()) : getForgerockErrorMessage(callback);

const trackLoginFailure = (stage: Stage, mobile: boolean, origin: Origin) => {
  if (['Connexion Page', 'Associate Account'].includes(stage)) {
    const path = TrackingService.buildPath(stage, mobile, origin);
    const eventId = TrackingService.buildEventIdClick(stage, 'failure', origin);
    TrackingService.triggerTrackingClic(eventId, path);
  }
};

const buildButtonTextOTP = (stage: Stage, mobile = false) => {
  if (stage === 'Retry or resend OTP') {
    return 'Demander un nouveau code';
  }

  return mobile ? 'Code de connexion par e-mail' : 'Recevoir un code de connexion par e-mail';
};

export const mapCallbackToComponent = (
  callback: FRCallback,
  stage: Stage,
  origin: Origin,
  mobile = false,
  isSocial = false,
): JSX.Element | undefined => {
  if (callback instanceof AttributeInputCallback<never>) {
    return mapAttributeInputCallbackToComponent(callback);
  }
  if (callback instanceof TextOutputCallback && callback.getMessage() !== DEFAULT_MESSAGE) {
    const message = callback.getMessage();
    if (callback.getMessageType() === MESSAGE_TYPE_SUCCESS) {
      if (message !== MESSAGE_VALUE_ACCOUNT_LOCKED) {
        if (message === MESSAGE_VALUE_BLOCTEL_LEGAL_MENTION) {
          return <BloctelLegalMention />;
        } else if (stage === 'Associate Account') {
          return <AssociateAccountIntroduction key="associate_account_introduction_top" email={message} />;
        }
        return <EditablePreviousInput message={message} readonly={isSocial} key={callback.payload._id} />;
      }
      return (
        <div key="blocked-account" className="text-sonic-silver text-sm">
          Par mesure de sécurité, votre compte a été temporairement bloqué.
          <br />
          <br />
          Vous pouvez cependant le réactiver en réinitialisant votre mot de passe via le bouton ci-dessous&nbsp;:
        </div>
      );
    } else if (callback.getMessageType() === MESSAGE_TYPE_ERROR) {
      trackLoginFailure(stage, mobile, origin);
      return <ErrorMessage message={getErrorMessage(callback)} key={callback.payload._id || 'error'} />;
    }
  }
  if (callback instanceof PasswordCallback) {
    if (stage === 'OTP Page') {
      return <OneTimePassword key={callback.payload._id} callback={callback} />;
    } else {
      return (
        <Password
          placeholder="Mot de passe"
          onChange={(event) => callback.setPassword(event.target.value)}
          key={callback.payload._id}
          autoFocus={stage === 'Connexion Page'}
        />
      );
    }
  }
  if (callback instanceof ValidatedCreatePasswordCallback) {
    return (
      <PasswordCreation
        placeholder={callback.getPrompt()}
        onChange={(event) => callback.setPassword(event.target.value)}
        key={callback.payload._id}
        required
      />
    );
  }

  if (callback instanceof SelectIdPCallback) {
    callback.setProvider('localAuthentication');
  }

  if (isOtpCallback(callback)) {
    return (
      <SubmitButton
        id={`${callback.payload._id}`}
        key={callback.payload._id + BOTTOM}
        variant="tertiary"
        stage={stage}
        origin={origin}
        callback={callback}
        mobile={mobile}
      >
        {buildButtonTextOTP(stage, mobile)}
      </SubmitButton>
    );
  }

  if (callback instanceof SelectIdPCallback) {
    return <SocialButtons key={callback.payload._id + BOTTOM} selectIdPCallback={callback} stage={stage} origin={origin} mobile={mobile} />;
  }

  if (callback instanceof ReCaptchaCallback) {
    return <Captcha key={callback.payload._id} callback={callback} />;
  }
};
